import React from 'react'
import { Card, TitleText, SubtitleText } from '@/components/ui'
import { Layout } from '../layout/Layout'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/utils/cn'

export interface PrivacyPolicyPageProps {
  className?: string
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ className }) => {
  const { isDark } = useTheme()

  return (
    <Layout className={className}>
      <div className="min-h-screen pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 mt-16">
            <TitleText>
              Privacy Policy – Biz Group Pty Ltd Trading As BiteWise Medical
            </TitleText>
            <p className={cn(
              "text-lg mt-4",
              isDark ? "text-secondary-300" : "text-secondary-600"
            )}>
              This document sets out our policy on the management of personal information which we
              have about individuals. Those individuals include medical practitioners, patients and
              dietitians.
            </p>
          </div>

          {/* Content */}
          <Card className="p-8 space-y-8">
            {/* Section 1 */}
            <section>
              <SubtitleText>
                1. Our privacy assurance to you
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  Your privacy is and will always be important to us. We respect your right to be aware of who
                  has information about you, what they are doing with it and why, and who else they are
                  sharing it with. We have adopted a privacy compliance culture that cements this
                  relationship with you. Its foundation is the <em>Privacy Act 1988</em> (as amended by the <em>Privacy
                  Amendment (Enhancing Privacy Protection) Act 2012</em>) and the <em>Privacy Regulation 2013</em>.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <SubtitleText>
                2. Overview
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  This privacy policy explains how we manage personal information. In particular it explains,
                  in relation to that personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>the kinds of personal information we collect and hold;</li>
                  <li>how we collect the personal information;</li>
                  <li>the purposes for which we collect, hold, use and disclose the personal information;</li>
                  <li>how an individual may access personal information about the individual that we
                      hold and seek the correction of that information;</li>
                  <li>how an individual may complain about a breach of the Australian Privacy Principles;
                      and</li>
                  <li>whether we are likely to disclose the personal information to overseas recipients
                      and the countries where those recipients are likely to be located.</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <SubtitleText>
                3. The kinds of personal information we collect and hold
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  Personal information is information about an identified individual or an individual who is
                  reasonably identified. We collect and hold personal information about individuals who are
                  medical practitioners, dietitians and patients. This information includes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>identification information such as the individual's name, date of birth, marital status and
                      contact details (including address, phone number and email address); and</li>
                  <li>any other information that we consider necessary to provide our services.</li>
                </ul>
                <p>
                  We will also collect sensitive health information regarding patients (such as height, weight,
                  body mass and health conditions), which is protected under the <em>Privacy Act</em>, because it is
                  essential for us to provide our services. We will only collect sensitive health information
                  from patients with their consent, except where otherwise permitted by law.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <SubtitleText>
                4. How we collect personal information
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  We collect personal information about individuals in a variety of ways. For example, we may
                  obtain personal information about patients from the individual themselves or from a
                  medical practitioner or dietitian who is treating the patient. When it is possible and practical
                  we will collect the information direct from a patient. When it is not practical or reasonable
                  to do so we will collect the information from a third party such as a medical practitioner or
                  dietitian. The third party could also be an authorised representative (such as a broker,
                  agent, or contractor), a related body, a referee, an employer or a government body.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <SubtitleText>
                5. How we hold personal information
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  We take all reasonable steps to ensure that an individual's personal information which we
                  hold is protected from misuse, interference or loss and from unauthorised access,
                  modification or disclosure. We do this by having physical, electronic and procedural
                  safeguards which protect the personal information we hold. For example, the personal
                  information is stored in secure Australian servers. Logins and passwords are required to
                  access our electronic databases. Our staff are required to maintain the confidentiality of
                  personal information and access to personal information is restricted to persons who
                  require the information to perform their duties. Where practical, we only keep personal
                  information for as long as is required to meet our legal requirements or internal needs.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <SubtitleText>
                6. The purposes for which we collect, hold, use and disclose personal information
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  We collect, hold, use and disclose personal information on individuals for purposes required
                  or permitted by law and which are reasonably necessary for our business activities. Those
                  purposes include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>the purpose for which the personal information was initially collected (including sharing
                      with related entities);</li>
                  <li>a purpose related to the initial purpose of collection if we consider that other purpose
                      would be within your reasonable expectations (or your consent is first obtained);</li>
                  <li>to deal with complaints;</li>
                  <li>to meet our legal and regulatory requirements;</li>
                  <li>to assist with a confidential alternative dispute resolution process.</li>
                </ul>
                <p>
                  We collect, hold, use and disclose personal information so that we can manage and
                  administer the services which we provide. To provide our services in the most cost effective
                  and efficient way we may decide to utilise the services of others. For example, the personal
                  information of medical practitioners, patients and dietitians will be provided to each other
                  so that they can make contact with each other for the purposes of creating dietary and meal
                  plans for patients, taking into account the patient's particular health concerns. We will
                  require all those other persons to respect your right of privacy. Personal information may
                  also be used or disclosed to tell an individual about products or services that may be of
                  interest to that individual. If the individual does not want their personal information used
                  for these direct marketing purposes the individual can "opt-out" by sending an email to
                  support@bitewise.com.au or by writing to us at:
                </p>
                <div className={cn(
                  "p-4 rounded-lg",
                  isDark ? "bg-secondary-800" : "bg-secondary-50"
                )}>
                  <p>Privacy Contact Officer</p>
                  <p>Name: Courtney Stewart</p>
                  <p>Address: Level 34, 1 Eagle Street, Brisbane, QLD, 4000.</p>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <SubtitleText>
                7. How an individual may access personal information
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  An individual may access personal information about the individual which we hold. The
                  individual can obtain that access by contacting our privacy contact officer as follows:
                </p>
                <div className={cn(
                  "p-4 rounded-lg",
                  isDark ? "bg-secondary-800" : "bg-secondary-50"
                )}>
                  <p><strong>Telephone:</strong> +61 422 395 883</p>
                  <p><strong>Email:</strong> support@bitewise.com.au</p>
                  <p><strong>Mail:</strong> Level 34, 1 Eagle Street, Brisbane, QLD, 4000.</p>
                </div>
                <p>
                  We will need to verify the individual's identity before giving access. We will usually provide
                  the requested personal information within 30 days of receiving the request. There is no
                  charge to make a request but we may levy an administration fee for providing access. If
                  there is a reason why we do not make the requested personal information available we will
                  provide our reason in writing. Such circumstances may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>access would create a serious threat to safety;</li>
                  <li>providing access will have an unreasonable impact upon the privacy of other
                      individuals;</li>
                  <li>denying access is required or authorised by law;</li>
                  <li>the request is frivolous;</li>
                  <li>legal proceedings are underway;</li>
                  <li>negotiations may be prejudiced by such access;</li>
                  <li>access would reveal a commercially sensitive decision-making process.</li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <SubtitleText>
                8. How an individual may seek the correction of personal information
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  If an individual considers that any personal information which we hold about the individual
                  is incorrect in any way the individual may ask us to correct that personal information. To
                  seek the correction, please contact our Privacy Contact Officer on the telephone number or
                  at the email or postal address above. In certain situations we may decide not to agree to a
                  request to correct personal information. We will tell you in writing why we have not agreed
                  to the correction request.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <SubtitleText>
                9. How an individual may complain and how we will deal with the complaint
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  We have an internal dispute resolution system that covers complaints. If an individual
                  considers that we have failed to comply with Division 3 of Part IIIA of the <em>Privacy Act 1988</em> or
                  the Australian Privacy he or she should contact our Privacy Contact Officer on the
                  telephone number or at the email or postal address above. We will then follow our internal
                  dispute resolution system. We will acknowledge the complaint within 7 days. A decision
                  will be made and advised within 30 days or a longer period as may be agreed with the
                  individual. If the individual is not satisfied with the decision he or she may make a complaint
                  to the Office of the Australian Information Commissioner (the "OAIC"). The contact details
                  for the OAIC are:
                </p>
                <div className={cn(
                  "overflow-x-auto rounded-lg border",
                  isDark ? "border-secondary-700" : "border-secondary-200"
                )}>
                  <table className={cn(
                    "w-full",
                    isDark ? "bg-secondary-800" : "bg-secondary-50"
                  )}>
                    <tbody>
                      <tr className={cn(
                        isDark ? "border-secondary-700" : "border-secondary-200"
                      )}>
                        <td className={cn(
                          "px-4 py-2 font-semibold",
                          isDark ? "text-secondary-200" : "text-secondary-700"
                        )}>
                          Telephone
                        </td>
                        <td className={cn(
                          "px-4 py-2",
                          isDark ? "text-secondary-300" : "text-secondary-700"
                        )}>
                          1300 363 992
                        </td>
                      </tr>
                      <tr className={cn(
                        isDark ? "border-secondary-700" : "border-secondary-200"
                      )}>
                        <td className={cn(
                          "px-4 py-2 font-semibold",
                          isDark ? "text-secondary-200" : "text-secondary-700"
                        )}>
                          Facsimile
                        </td>
                        <td className={cn(
                          "px-4 py-2",
                          isDark ? "text-secondary-300" : "text-secondary-700"
                        )}>
                          (02) 9284 9666
                        </td>
                      </tr>
                      <tr className={cn(
                        isDark ? "border-secondary-700" : "border-secondary-200"
                      )}>
                        <td className={cn(
                          "px-4 py-2 font-semibold",
                          isDark ? "text-secondary-200" : "text-secondary-700"
                        )}>
                          Website
                        </td>
                        <td className={cn(
                          "px-4 py-2",
                          isDark ? "text-secondary-300" : "text-secondary-700"
                        )}>
                          www.oaic.gov.au
                        </td>
                      </tr>
                      <tr>
                        <td className={cn(
                          "px-4 py-2 font-semibold align-top",
                          isDark ? "text-secondary-200" : "text-secondary-700"
                        )}>
                          Mail
                        </td>
                        <td className={cn(
                          "px-4 py-2",
                          isDark ? "text-secondary-300" : "text-secondary-700"
                        )}>
                          <div>
                            <p>The Office of the Australian Information Commissioner</p>
                            <p>GPO Box 5218</p>
                            <p>SYDNEY NSW 2001</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <SubtitleText>
                10. Disclosure of personal information to overseas recipients
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  Generally, we will retain your personal information within Australia, and it is not likely that
                  we will use or disclose it overseas. In the unlikely event that the information may need to be
                  transferred overseas in order to perform one of our functions or activities, we will either
                  obtain your express or implied consent or will use our best endeavours to ensure that your
                  personal information will receive protection similar to that which it would have if the
                  information were in Australia.
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <SubtitleText>
                11. Definitions
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>In this privacy policy:</p>
                <p>
                  "we", "us" and "our" means Biz Group Pty Ltd ACN 671 345 181, a company incorporated in
                  Australia; and
                </p>
                <p>
                  any words which are defined in the <em>Privacy Act 1988</em> have the same meaning in this privacy
                  policy.
                </p>
              </div>
            </section>
          </Card>
        </div>
      </div>
    </Layout>
  )
}

export { PrivacyPolicyPage }